import { jsx, jsxs } from "@ecopages/jsx/jsx-runtime";
import { withDefaultAriaLabel } from "@/aria";
import { cx } from "@/lib/cx";
import { CAROUSEL_DEFAULTS } from "./carousel.script";
import "./carousel.script";
export function RuiCarouselSlide({
  id,
  children,
  class: className,
  ...props
}) {
  return /* @__PURE__ */jsx("div", {
    ...props,
    class: cx("rui-carousel__slide", className),
    "data-slide": id,
    children
  });
}
function toolbarPrevLabel() {
  return /* @__PURE__ */jsxs("span", {
    class: "rui-carousel__nav-label",
    children: [/* @__PURE__ */jsx("span", {
      class: "rui-carousel__nav-icon",
      "aria-hidden": "true",
      children: "\u2039"
    }), "Previous"]
  });
}
function toolbarNextLabel() {
  return /* @__PURE__ */jsxs("span", {
    class: "rui-carousel__nav-label",
    children: ["Next", /* @__PURE__ */jsx("span", {
      class: "rui-carousel__nav-icon",
      "aria-hidden": "true",
      children: "\u203A"
    })]
  });
}
export function RuiCarouselPrev({
  children,
  class: className,
  disabled,
  overlay,
  aria,
  ...props
}) {
  return /* @__PURE__ */jsx("button", {
    ...props,
    aria: withDefaultAriaLabel(aria, "Previous slide"),
    type: "button",
    "data-carousel-action": "prev",
    "data-ref": "prev",
    class: cx("rui-carousel__nav rui-button rui-button--outline rui-button--sm", overlay ? "rui-carousel__nav--overlay" : "rui-carousel__nav--toolbar", className),
    disabled,
    children: children ?? (overlay ? /* @__PURE__ */jsx("span", {
      class: "rui-carousel__nav-icon",
      "aria-hidden": "true",
      children: "\u2039"
    }) : toolbarPrevLabel())
  });
}
export function RuiCarouselNext({
  children,
  class: className,
  disabled,
  overlay,
  aria,
  ...props
}) {
  return /* @__PURE__ */jsx("button", {
    ...props,
    aria: withDefaultAriaLabel(aria, "Next slide"),
    type: "button",
    "data-carousel-action": "next",
    "data-ref": "next",
    class: cx("rui-carousel__nav rui-button rui-button--outline rui-button--sm", overlay ? "rui-carousel__nav--overlay" : "rui-carousel__nav--toolbar", className),
    disabled,
    children: children ?? (overlay ? /* @__PURE__ */jsx("span", {
      class: "rui-carousel__nav-icon",
      "aria-hidden": "true",
      children: "\u203A"
    }) : toolbarNextLabel())
  });
}
export function RuiCarouselRotation({
  children,
  class: className,
  overlay,
  aria,
  ...props
}) {
  return /* @__PURE__ */jsx("button", {
    ...props,
    aria: withDefaultAriaLabel(aria, "Start rotation"),
    type: "button",
    "data-carousel-action": "rotation",
    "data-ref": "rotation",
    class: cx("rui-carousel__rotation rui-button rui-button--ghost rui-button--sm", overlay && "rui-carousel__rotation--overlay", className),
    "aria-pressed": false,
    children: children ?? "Play"
  });
}
function CarouselShell({
  autoplay,
  children,
  controlsVariant,
  label,
  next,
  prev,
  rotation,
  showIndicators,
  showRotationControl,
  transition
}) {
  const overlay = controlsVariant === "overlay";
  const showRotation = autoplay || showRotationControl;
  const prevControl = prev ?? /* @__PURE__ */jsx(RuiCarouselPrev, {
    overlay
  });
  const nextControl = next ?? /* @__PURE__ */jsx(RuiCarouselNext, {
    overlay
  });
  const rotationControl = showRotation ? rotation ?? /* @__PURE__ */jsx(RuiCarouselRotation, {
    overlay
  }) : null;
  const indicators = showIndicators ? /* @__PURE__ */jsx("div", {
    class: cx("rui-carousel__indicators", overlay && "rui-carousel__indicators--overlay"),
    "data-ref": "indicators",
    role: "tablist",
    "aria-label": "Choose slide to display"
  }) : null;
  const toolbarControls = /* @__PURE__ */jsxs("div", {
    class: "rui-carousel__toolbar",
    children: [showRotation ? /* @__PURE__ */jsx("div", {
      class: "rui-carousel__toolbar-rotation",
      children: rotationControl
    }) : null, /* @__PURE__ */jsx("div", {
      class: "rui-carousel__toolbar-side rui-carousel__toolbar-side--start",
      children: prevControl
    }), /* @__PURE__ */jsx("div", {
      class: "rui-carousel__toolbar-center",
      children: indicators
    }), /* @__PURE__ */jsx("div", {
      class: "rui-carousel__toolbar-side rui-carousel__toolbar-side--end",
      children: nextControl
    })]
  });
  const overlayChrome = overlay ? /* @__PURE__ */jsxs("div", {
    class: "rui-carousel__overlay-chrome",
    children: [showRotation ? /* @__PURE__ */jsx("div", {
      class: "rui-carousel__overlay-rotation",
      children: rotationControl
    }) : null, /* @__PURE__ */jsxs("div", {
      class: "rui-carousel__controls rui-carousel__controls--overlay",
      children: [prevControl, nextControl]
    }), indicators]
  }) : null;
  return /* @__PURE__ */jsxs("section", {
    class: cx("rui-carousel", `rui-carousel--${transition}`, `rui-carousel--controls-${controlsVariant}`),
    "data-ref": "root",
    "aria-roledescription": "carousel",
    "aria-label": label,
    children: [/* @__PURE__ */jsx("div", {
      class: "rui-carousel__stage",
      children: /* @__PURE__ */jsxs("div", {
        class: "rui-carousel__viewport",
        "data-ref": "viewport",
        children: [/* @__PURE__ */jsx("div", {
          class: "rui-carousel__track",
          children
        }), overlayChrome]
      })
    }), !overlay ? /* @__PURE__ */jsx("div", {
      class: "rui-carousel__footer",
      children: toolbarControls
    }) : null]
  });
}
export function RuiCarousel({
  slides,
  children,
  prev,
  next,
  rotation,
  label = CAROUSEL_DEFAULTS.label,
  transition = CAROUSEL_DEFAULTS.transition,
  controlsVariant = CAROUSEL_DEFAULTS.controlsVariant,
  showIndicators,
  showRotationControl,
  autoplay,
  ...props
}) {
  const slideList = slides ?? [];
  const slideContent = slides != null ? slideList.map(slide => /* @__PURE__ */jsx(RuiCarouselSlide, {
    id: slide.id,
    children: slide.children
  })) : children;
  return /* @__PURE__ */jsx("rui-carousel", {
    ...props,
    label,
    transition,
    controlsVariant,
    showIndicators,
    showRotationControl,
    autoplay,
    slideCount: slideList.length,
    children: /* @__PURE__ */jsx(CarouselShell, {
      autoplay,
      controlsVariant,
      label,
      next,
      prev,
      rotation,
      showIndicators,
      showRotationControl,
      transition,
      children: slideContent
    })
  });
}